import { useCallback, useState } from 'react';

const STORAGE_KEY = 'platform.sections';

function readAll(): Record<string, boolean> {
  try {
    const parsed: unknown = JSON.parse(globalThis.localStorage?.getItem(STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

/** Reads the remembered open state of a collapsible section (undefined = never chosen). */
export function readRememberedOpen(id: string): boolean | undefined {
  const value = readAll()[id];
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * Open/closed state of a collapsible section, remembered per browser under `id`
 * (DESIGN.md T5). A per-viewer convenience: storage failures fall back to `defaultOpen`.
 */
export function useRememberedOpen(id: string, defaultOpen: boolean): [boolean, (open: boolean) => void] {
  const [open, setOpenState] = useState(() => readRememberedOpen(id) ?? defaultOpen);
  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      try {
        globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify({ ...readAll(), [id]: next }));
      } catch {
        // Not remembered; still applied for this view.
      }
    },
    [id],
  );
  return [open, setOpen];
}
